import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import { Bar, Button, Card, Chip, ConfirmDialog, Countdown, ErrorNotice, Notice, Skeleton, Stat, Table, Timeline } from '@/components/ui';
import { useI18n, formatRelative } from '@/i18n';
import { api } from '@/lib/api';
import { ME_KEY, type Me } from '@/lib/auth';
import { dateOnly, money, slotLabel } from '@/lib/format';
import { usePublicSettings } from '@/lib/settings';
import type { BR, BrokerDashboard as Dash, VE } from '@/lib/types';
import { AssignmentErrorNotice, assignmentFailure } from '@/features/shared/RequestParts';
import { BROKER_ACTIVE, BrokerBrChip, useNextStep } from './common';

export function BrokerDashboard({ me }: { me: Me }) {
  const { t, locale, localTitle } = useI18n();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const nextStep = useNextStep();
  const [declining, setDeclining] = useState<BR | null>(null);
  const [error, setError] = useState<string | null>(null);
  const dash = useQuery({ queryKey: ['broker', 'dashboard'], queryFn: () => api.get<Dash>('/api/broker/dashboard'), refetchInterval: 15_000 });
  const active = useQuery({ queryKey: ['broker', 'requests', 'active'], queryFn: () => api.get<{ items: BR[] }>(`/api/broker/requests?status=${BROKER_ACTIVE.join(',')}&limit=6`) });
  const viewings = useQuery({ queryKey: ['broker', 'viewings', 'mine'], queryFn: () => api.get<{ items: VE[] }>('/api/broker/viewings?status=with_broker&limit=5') });
  const commissions = useQuery({ queryKey: ['broker', 'commissions'], queryFn: () => api.get<{ items: BR[] }>('/api/broker/commissions') });
  const { acceptWindowMinutes: windowMin } = usePublicSettings();
  const refresh = () => { void qc.invalidateQueries({ queryKey: ['broker'] }); void qc.invalidateQueries({ queryKey: ME_KEY }); };
  const act = useMutation({
    mutationFn: (input: { id: string; action: 'accept' | 'decline'; reason?: string }) => api.post(`/api/broker/requests/${input.id}/${input.action}`, input.action === 'decline' ? { reason: input.reason } : {}),
    onSuccess: (_r, input) => { setDeclining(null); setError(null); refresh(); if (input.action === 'accept') navigate(`/broker/requests/${input.id}`); },
    onError: (e) => { setDeclining(null); setError(assignmentFailure(e)); refresh(); },
  });
  if (dash.isLoading) return <Skeleton lines={8} />;
  if (dash.error || !dash.data) return <ErrorNotice error={dash.error} retry={() => void dash.refetch()} labels={{ message: t('app.error'), retry: t('app.retry') }} />;
  const d = dash.data;
  const dueAmount = (commissions.data?.items ?? []).reduce((s, r) => s + (r.commissionHalalas ?? 0), 0);
  const nearest = d.incoming.map((r) => r.acceptDeadlineAt).filter(Boolean).sort()[0];
  const offersPending = (active.data?.items ?? []).filter((r) => r.status === 'offer_pending').length;
  const tier = me.broker?.slots.tier;
  const today = new Intl.DateTimeFormat(locale === 'ar' ? 'ar-SA-u-nu-latn-ca-gregory' : 'en-GB', { dateStyle: 'full', timeZone: 'Asia/Riyadh' }).format(new Date());
  return (
    <div className="flex flex-col gap-7">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[13px] text-muted mb-2">{today}</p>
          <h1>{t('dashboard.welcome', { name: me.user.name.split(' ')[0] ?? '' })}</h1>
        </div>
        <Link to="/broker/membership">
          <Button variant="secondary" className="min-h-9 text-[14px]">{tier && me.broker?.membershipStatus === 'active' ? t(me.broker.autoRenew === false ? 'broker.membership.tierEndsOn' : 'dashboard.renewsOn', { tier: locale === 'ar' ? tier.nameAr : tier.nameEn, date: dateOnly(me.broker.membershipPeriodEnd, locale) }) : t('dashboard.chooseMembership')}</Button>
        </Link>
      </div>
      {d.membershipLocked ? (
        <Notice tone="warning" title={t('dashboard.membershipLocked')}>
          <Link to="/broker/membership">{t('dashboard.chooseMembership')}</Link>
        </Notice>
      ) : null}
      <AssignmentErrorNotice error={error} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-sm border border-line px-5 py-4">
          <p className="label">{t('dashboard.slots')}</p>
          <p className="mt-1 text-[28px] font-light leading-tight num">{d.slots.used} / {d.slots.max}</p>
          <p className="text-[13px] text-muted mt-1">{t('dashboard.slotsFree', { n: d.slots.free })}</p>
          <Bar value={d.slots.used} max={d.slots.max} className="mt-2" />
        </div>
        <Stat label={t('dashboard.incoming')} value={d.incoming.length} tone={d.incoming.length ? 'warning' : undefined} hint={nearest ? undefined : ''} />
        <Stat label={t('dashboard.active')} value={d.activeCount} hint={offersPending ? t('dashboard.offerAwaitingOwner', { n: offersPending }) : undefined} />
        <Stat label={t('dashboard.commissionDue')} value={money(dueAmount, locale)} tone={dueAmount ? 'warning' : undefined} hint={commissions.data?.items[0] ? t('dashboard.slotHeldUntilPaid', { number: commissions.data.items[0].number }) : undefined} />
      </div>
      <Card lift>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <h2>{t('dashboard.awaitingYou')}</h2>
          <span className="text-[13px] text-muted">{t('dashboard.awaitingHint', { n: windowMin })}</span>
        </div>
        {d.incoming.length ? (
          <ul className="flex flex-col">
            {d.incoming.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center justify-between gap-3 py-3.5 border-t border-line">
                <div className="min-w-0">
                  <span className="num text-muted text-[13px]">{r.number}</span>
                  <p className="font-medium text-[16px]">{localTitle(r)}{r.property ? <span> · <span className="num">{r.property.areaM2} {t('app.m2')}</span></span> : null}</p>
                  <p className="text-muted text-[14px]">
                    {r.property?.ownerEstimateHalalas != null ? <span>{t('dashboard.ownerEstimate')} <span className="num">{money(r.property.ownerEstimateHalalas, locale)}</span> · </span> : null}
                    {t('app.owner')} {r.ownerName ?? '—'} · <span className="ltr">+966 5• ••• ••••</span> ({t('dashboard.phoneAfterAccept')})
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {r.acceptDeadlineAt ? <Chip form="warning"><Countdown deadline={r.acceptDeadlineAt} expiredLabel={t('dashboard.expired')} /></Chip> : null}
                  <Button variant="accent" loading={act.isPending && act.variables?.id === r.id} onClick={() => act.mutate({ id: r.id, action: 'accept' })}>{t('dashboard.accept')}</Button>
                  <Button variant="secondary" onClick={() => setDeclining(r)}>{t('dashboard.decline')}</Button>
                </div>
              </li>
            ))}
          </ul>
        ) : <p className="text-muted text-[14px]">{t('broker.incoming.empty')}</p>}
      </Card>
      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6 items-start">
        <Card>
          <div className="flex items-center justify-between mb-3"><h2>{t('dashboard.yourActive')}</h2><Link to="/broker/requests">{t('app.viewAll')}</Link></div>
          {active.data?.items.length ? (
            <Table>
              <thead><tr><th>{t('app.number')}</th><th>{t('app.property')}</th><th>{t('app.status')}</th><th>{t('app.nextStep')}</th></tr></thead>
              <tbody>
                {active.data.items.map((r) => {
                  const ns = nextStep(r);
                  return (
                    <tr key={r.id}><td className="num"><Link to={`/broker/requests/${r.id}`}>{r.number}</Link></td><td>{localTitle(r)}</td><td><BrokerBrChip br={r} /></td><td className={ns.muted ? 'text-muted' : ''}>{ns.muted ? ns.text : <Link to={`/broker/requests/${r.id}`}>{ns.text}</Link>}</td></tr>
                  );
                })}
              </tbody>
            </Table>
          ) : <p className="text-muted text-[14px]">{t('broker.requests.empty')}</p>}
        </Card>
        <div className="flex flex-col gap-3">
          <Card>
            <h2 className="mb-3">{t('dashboard.viewingsWaiting')}</h2>
            {viewings.data?.items.length ? (
              <Timeline items={viewings.data.items.map((v) => ({ id: v.id, at: v.updatedAt, tone: 'warning' as const, title: <span>{localTitle(v)} · <span className="tabular-nums">{slotLabel(v.slotStart, locale)}</span></span>, body: <span>{t('dashboard.viewingBody')} <Link to="/broker/viewings">{t('dashboard.approveOrPropose')}</Link></span>, who: v.number }))} />
            ) : <p className="text-muted text-[14px]">{t('broker.viewings.empty')}</p>}
          </Card>
          {commissions.data?.items[0] ? (
            <Card>
              <h2 className="mb-2">{t('dashboard.commissionDue')}</h2>
              <p className="text-[14px] text-fg-2">{t('dashboard.commissionCard', { number: commissions.data.items[0].number, sale: money(commissions.data.items[0].commissionHalalas ? commissions.data.items[0].commissionHalalas * 200 : null, locale), amount: money(commissions.data.items[0].commissionHalalas, locale) })}</p>
              <Link to="/broker/commissions"><Button variant="secondary" className="w-full mt-3">{t('dashboard.paymentSteps')}</Button></Link>
              <p className="text-[12px] text-muted mt-2">{formatRelative(commissions.data.items[0].completedAt ?? commissions.data.items[0].updatedAt, locale)}</p>
            </Card>
          ) : null}
        </div>
      </div>
      <DeclineDialog br={declining} onClose={() => setDeclining(null)} loading={act.isPending} onConfirm={(reason) => declining && act.mutate({ id: declining.id, action: 'decline', reason })} />
    </div>
  );
}

export function DeclineDialog({ br, onClose, onConfirm, loading }: { br: BR | null; onClose: () => void; onConfirm: (reason: string) => void; loading: boolean }) {
  const { t } = useI18n();
  return <ConfirmDialog open={!!br} onClose={onClose} title={t('broker.incoming.declineTitle', { number: br?.number ?? '' })} body={t('broker.incoming.declineHint')} confirmLabel={t('dashboard.decline')} cancelLabel={t('app.back')} reason={{ label: t('app.reasonOptional'), placeholder: [t('broker.incoming.reasons.outOfArea'), t('broker.incoming.reasons.noSlot'), t('broker.incoming.reasons.propertyType')].join(' · ') }} loading={loading} onConfirm={onConfirm} />;
}
